import { StateCreator } from 'zustand';

import { PlankSelectionItem, PlankSelectionStore } from './types/PlankSelectionStore';

/** Rebuilds the per-column key registry, dropping selected rows that no longer exist anywhere.
 * Plank data is refetched periodically and a selected row can disappear (a conflict is
 * resolved, a defect moves to onderhoud); without this it would linger invisibly and silently
 * turn the next single drag into a batch drop. */
const withScopeKeys = (
  state: PlankSelectionStore,
  scopeId: string,
  keys: Set<string> | null,
): Partial<PlankSelectionStore> => {
  const plankSelectableKeysByScope = new Map(state.plankSelectableKeysByScope);
  if (keys) plankSelectableKeysByScope.set(scopeId, keys);
  else plankSelectableKeysByScope.delete(scopeId);

  const allSelectableKeys = new Set([...plankSelectableKeysByScope.values()].flatMap((s) => [...s]));
  const isSelectable = (key: string) => allSelectableKeys.has(key);
  const { plankSelection, plankSelectionAnchorKey } = state;
  const pruned = new Map([...plankSelection].filter(([key]) => isSelectable(key)));
  return {
    plankSelectableKeysByScope,
    plankSelection: pruned.size === plankSelection.size ? plankSelection : pruned,
    plankSelectionAnchorKey:
      plankSelectionAnchorKey && isSelectable(plankSelectionAnchorKey) ? plankSelectionAnchorKey : null,
  };
};

/**
 * Store slice for multi-selecting Plank rows with Ctrl/Shift clicks, so several items can be
 * dragged to MWD in one drop. Deliberately not persisted (see usePlankTableStore's
 * `partialize`): transient UI state that should reset on reload.
 */
const plankSelectionStateCreator: StateCreator<PlankSelectionStore> = (set, get) => ({
  plankSelection: new Map<string, PlankSelectionItem>(),
  plankSelectionAnchorKey: null,
  plankSelectableKeysByScope: new Map<string, Set<string>>(),

  isPlankItemSelected: (key) => get().plankSelection.has(key),

  togglePlankItemSelection: (item) => {
    set((state) => {
      const plankSelection = new Map(state.plankSelection);
      if (plankSelection.has(item.key)) plankSelection.delete(item.key);
      else plankSelection.set(item.key, item);
      return { plankSelection, plankSelectionAnchorKey: item.key };
    });
  },

  selectPlankItemRange: (items, scopeKeys) => {
    // Replaces the selection within this column only, so dragging the range's edge back
    // towards the anchor shrinks it instead of only ever growing, while any rows selected in
    // other columns (via Ctrl-click) stay part of the batch. The anchor stays put, so every
    // Shift-click measures from the same row.
    set((state) => {
      const plankSelection = new Map([...state.plankSelection].filter(([key]) => !scopeKeys.has(key)));
      items.forEach((item) => plankSelection.set(item.key, item));
      return {
        plankSelection,
        plankSelectionAnchorKey: state.plankSelectionAnchorKey ?? items.at(-1)?.key ?? null,
      };
    });
  },

  clearPlankSelection: () => set({ plankSelection: new Map(), plankSelectionAnchorKey: null }),

  getPlankSelection: () => [...get().plankSelection.values()],

  syncPlankSelectableKeys: (scopeId, items) => {
    // Columns re-report every render with a freshly built array, so this must be a genuine
    // no-op when the keys are unchanged: writing regardless re-renders them and loops forever.
    // Comparing against the deduplicated key set (rather than items.length) keeps this a no-op
    // even when a column's rows momentarily contain a duplicate key: comparing raw lengths would
    // permanently disagree with the (deduplicated) stored Set and loop forever.
    const nextKeys = new Set(items.map((item) => item.key));
    const current = get().plankSelectableKeysByScope.get(scopeId);
    if (current?.size === nextKeys.size && [...nextKeys].every((key) => current.has(key))) return;

    set((state) => withScopeKeys(state, scopeId, nextKeys));
  },

  gcPlankScope: (scopeId) => {
    if (!get().plankSelectableKeysByScope.has(scopeId)) return;

    set((state) => withScopeKeys(state, scopeId, null));
  },
});

export default plankSelectionStateCreator;
